import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { NotAllowed, PageLoader } from '@components';
import { shareRequestAPI, shareRequestDecisionAPI } from '@constants';
import { useTitle } from '@hooks';
import { PageTitle } from 'constants/pageTitle';
import { formatDate } from '@utils';
import { buttonSizeSx, secondaryButtonSx } from 'theme/buttons';

/** What `GET /api/share/requests/:id` answers: who is asking, for what, and what was decided. */
interface IShareRequest {
  id: string;
  visitor: { email: string; name: string; nameSource: 'typed' | 'crm' };
  requestedAt: string;
  client: string;
  scope: 'client' | 'position' | 'submission';
  scopeLabel: string;
  permissions: 'H:R' | 'H:W';
  sharer: { name: string; email: string };
  alive: boolean;
  decision: 'approved' | 'declined' | null;
  decidedAt: string | null;
  /** Decided on this page, or closed by Rocky when the person was made a client contact. */
  decidedVia: 'dashboard' | 'rocky' | null;
  decidedBy: string | null;
  /** Rocky's Client contacts page, where an account is made. */
  contactsUrl: string;
  submissions: {
    submissionId: string;
    candidate: string;
    position: string;
    status: string;
  }[];
}

const SECONDARY_SX = { ...buttonSizeSx.md, ...secondaryButtonSx } as const;

const whatWasShared = (request: IShareRequest) =>
  request.scope === 'submission'
    ? `one submission: ${request.scopeLabel}`
    : request.scope === 'position'
      ? `the open submissions on ${request.scopeLabel}`
      : `every open submission at ${request.client}`;

/**
 * A delivery manager decides on someone's request to see shared candidates. Guest access is
 * a grant of the manager's own, for that address, covering exactly what the original share
 * covered; the record then says who gave access to whom. The other answer is an account:
 * the manager adds the person as a client contact in Rocky, which tells the dashboard and
 * closes the request by itself.
 */
export const ShareRequestPage: React.FC = () => {
  const setPageTitle = useTitle();
  const { requestId } = useParams<{ requestId: string }>();
  const [request, setRequest] = useState<IShareRequest | null>(null);
  const [loadError, setLoadError] = useState<'forbidden' | 'missing' | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setPageTitle(PageTitle.SHARE_REQUEST);
  }, [setPageTitle]);

  useEffect(() => {
    if (!requestId) {
      setLoadError('missing');

      return;
    }
    let cancelled = false;
    axios
      .get<IShareRequest>(shareRequestAPI(requestId), { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setRequest(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(e?.response?.status === 403 ? 'forbidden' : 'missing');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const decide = useCallback(
    async (decision: 'approve' | 'decline') => {
      if (!requestId) return;
      setSubmitting(true);
      setError(null);
      try {
        const { data } = await axios.post<IShareRequest>(
          shareRequestDecisionAPI(requestId, decision),
          {},
          { withCredentials: true },
        );
        setRequest(data);
      } catch (e: any) {
        const message = e?.response?.data?.message;
        setError(
          typeof message === 'string'
            ? message
            : 'We could not record your decision. Please try again.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [requestId],
  );

  if (loadError === 'forbidden') return <NotAllowed />;

  if (loadError === 'missing') {
    return (
      <Stack px={{ xs: 2, tablet: 3 }} py={3} gap={2} maxWidth="48rem">
        <Typography variant="h2" component="h1">
          Access request
        </Typography>
        <Typography variant="body1" color="text.secondary">
          This request is not available. It may belong to an account you do not
          manage.
        </Typography>
      </Stack>
    );
  }

  if (!request) return <PageLoader />;

  const decided = request.decision !== null;

  return (
    <Stack px={{ xs: 2, tablet: 3 }} py={3} gap={2} maxWidth="48rem">
      <Box>
        <Typography variant="h2" component="h1">
          Access request
        </Typography>
        <Typography variant="body1" color="text.secondary" mt={0.5}>
          Someone opened a link to shared candidates at {request.client}, but
          the link was shared with a different address.
        </Typography>
      </Box>

      <Paper elevation={5} sx={{ p: 3 }}>
        <Stack gap={2}>
          <Box
            display="grid"
            gridTemplateColumns="max-content 1fr"
            columnGap={3}
            rowGap={1}
          >
            <Typography variant="body2" color="text.secondary">
              Name
            </Typography>
            <Typography variant="body1">{request.visitor.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1">{request.visitor.email}</Typography>
            <Typography variant="body2" color="text.secondary">
              Asked for
            </Typography>
            <Typography variant="body1">{whatWasShared(request)}</Typography>
            <Typography variant="body2" color="text.secondary">
              Shared by
            </Typography>
            <Typography variant="body1">
              {request.sharer.name} ({request.sharer.email})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Asked on
            </Typography>
            <Typography variant="body1">
              {formatDate(request.requestedAt)}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary">
            {request.visitor.nameSource === 'crm'
              ? 'The address belongs to a client contact in CRM, and the name is from CRM. '
              : 'They typed the name and the address themselves; '}
            Only the address will be verified, with a code, before anything
            opens. Check with {request.sharer.name} or the client that this
            person should see candidates before you decide.
          </Typography>

          <Box>
            <Typography variant="h3" component="h2" mb={1}>
              What they would see ({request.submissions.length})
            </Typography>
            <Stack component="ul" gap={0.5} pl={2.5} m={0}>
              {request.submissions.map((s) => (
                <Typography key={s.submissionId} component="li" variant="body1">
                  <strong>{s.candidate}</strong>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {' '}
                    &middot; {s.position} &middot; {s.status}
                  </Typography>
                </Typography>
              ))}
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={1}>
              {request.permissions === 'H:W'
                ? 'They could read each profile, leave feedback with a decision, ask a question, and move or cancel an interview, as the sharer can.'
                : 'They could read each profile. Decisions would stay with the sharer.'}
              {request.decidedVia !== 'rocky' &&
                ' Guest access ends by itself after seven days.'}
            </Typography>
          </Box>

          {decided ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircleOutlineIcon
                color={request.decision === 'approved' ? 'success' : 'disabled'}
              />
              <Typography variant="body1">
                {request.decidedVia === 'rocky'
                  ? 'Added as a client contact in Rocky'
                  : request.decision === 'approved'
                    ? 'Approved'
                    : 'Declined'}
                {request.decidedBy && ` by ${request.decidedBy}`}
                {request.decidedAt && ` on ${formatDate(request.decidedAt)}`}.
                {request.decision === 'approved' &&
                  (request.decidedVia === 'rocky'
                    ? ` ${request.visitor.email} now has an account and was emailed these candidates as dashboard pages.`
                    : ` ${request.visitor.email} was emailed their own links. The record says you gave this access.`)}
              </Typography>
            </Box>
          ) : !request.alive ? (
            <Typography variant="body1" color="text.secondary">
              The share this request came from has ended, so there is nothing to
              give guest access to. Ask {request.sharer.name} to share again, or
              add this person as a client contact in Rocky if they should have
              an account.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Two ways to let them in. <strong>Give guest access</strong> to
                exactly what {request.sharer.name} shared: they get an email
                with the links, prove the address with a code, and the access
                ends after seven days. Or <strong>add them in Rocky</strong> as
                a client contact if they should have an account: Rocky tells the
                dashboard, this request closes on its own, and they are emailed
                these candidates as dashboard pages.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1.5} pt={1}>
                <Button
                  variant="contained"
                  sx={buttonSizeSx.md}
                  disabled={submitting}
                  onClick={() => void decide('approve')}
                >
                  Give guest access
                </Button>
                <Button
                  variant="outlined"
                  sx={SECONDARY_SX}
                  href={request.contactsUrl}
                  target="_blank"
                  rel="noopener"
                >
                  Add as a client contact in Rocky
                </Button>
                <Button
                  variant="outlined"
                  sx={SECONDARY_SX}
                  disabled={submitting}
                  onClick={() => void decide('decline')}
                >
                  Decline
                </Button>
              </Box>
            </>
          )}
          {error && (
            <Typography color="text.danger" variant="caption">
              {error}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
};
